'use client'

import { cn } from '@/lib/utils'
import { AllPurposeLink } from '@components/all-purpose-link'
import { buttonVariants } from '@components/button'
import { OverlineHeader } from '@components/overline-header'
import {
  ArrowRightIcon,
  BodyText,
  DetailText,
  DisplayText,
  HeadlineText,
  JumboText
} from 'design-configuration/components'
import { BentoItemFragment } from '@/gql/generated/graphql'
import { Bento500 } from './bento-500'
import { motion, useInView } from 'motion/react'
import { useRef, useEffect, useState } from 'react'
import { ab500DiscoverRoute, ab500LearnMoreRoute, ab500TimelineRoute } from '@utils/routes'

export function AB500Banner({ items }: { items: BentoItemFragment[] }): React.ReactElement {
  // Separate refs for each section for better scroll-triggered animations
  const headerRef = useRef(null)
  const countdownRef = useRef(null)
  const featuredRef = useRef(null)

  // Each section has its own visibility check
  const headerInView = useInView(headerRef, { once: true, amount: 0.3 })
  const countdownInView = useInView(countdownRef, { once: true, amount: 0.3 })
  const featuredInView = useInView(featuredRef, { once: true, amount: 0.3 })

  // State for animated numbers
  const [collectionCount, setCollectionCount] = useState(0)
  const [remainingCount, setRemainingCount] = useState(0)

  // Animate numbers when countdown section comes into view
  useEffect(() => {
    if (countdownInView) {
      // Start animating after a small delay
      const timer = setTimeout(() => {
        setCollectionCount(496)
        setRemainingCount(4)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [countdownInView])

  // Animation variants for fade in and up
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.21, 0.47, 0.32, 0.98]
      }
    }
  }

  return (
    <div className="banner-off-opacity p-md gap-2xl flex w-full flex-col">
      <div className="lg:gap-2xl gap-md flex flex-col lg:flex-row lg:items-end">
        <motion.div
          ref={headerRef}
          className="gap-md flex flex-grow flex-col"
          variants={fadeInUp}
          initial="hidden"
          animate={headerInView ? 'visible' : 'hidden'}
        >
          <div className="gap-sm flex flex-col">
            <img src="/ab500-logo.svg" alt="Art Blocks 500" className="w-full lg:h-[88px] lg:w-fit" />
            <DisplayText className="text-on-surface-inverted font-light">5 years. 500 projects.</DisplayText>
          </div>

          <div className="gap-xs flex flex-col">
            <AllPurposeLink href={ab500TimelineRoute()}>
              <LinkArrowItem title="View the interactive timeline" delay={0.2} isInView={headerInView} />
            </AllPurposeLink>
            <AllPurposeLink href={ab500DiscoverRoute()}>
              <LinkArrowItem title="Explore the Art Blocks 500" delay={0.2} isInView={headerInView} />
            </AllPurposeLink>
            <AllPurposeLink href={ab500LearnMoreRoute()}>
              <LinkArrowItem title="Learn more on the Journal" delay={0.3} isInView={headerInView} />
            </AllPurposeLink>
          </div>
        </motion.div>
        <motion.div
          ref={countdownRef}
          className="gap-xs flex flex-col items-center lg:w-[286px]"
          variants={fadeInUp}
          initial="hidden"
          animate={countdownInView ? 'visible' : 'hidden'}
        >
          <DetailText className="text-on-surface-inverted self-start font-medium uppercase opacity-50 sm:self-center">
            The Art Blocks 500 Countdown
          </DetailText>
          <motion.div
            className="text-on-surface-inverted gap-md flex w-full justify-between font-light leading-[130%]"
            animate={collectionCount + remainingCount === 500 ? { opacity: 1 } : { opacity: 0 }}
          >
            <JumboText>{collectionCount}</JumboText>
            <JumboText>{remainingCount}</JumboText>
          </motion.div>
          <div className="gap-md -mt-sm flex w-full flex-col">
            <div className="gap-xs flex w-full flex-col">
              <div className="flex items-center justify-between opacity-50">
                <DetailText className="text-on-surface-inverted font-medium uppercase">Collections Released</DetailText>
                <DetailText className="text-on-surface-inverted font-medium uppercase">To go</DetailText>
              </div>
              <ChromieProgressBar />
            </div>

            <AllPurposeLink
              className={cn(
                buttonVariants({ colorTheme: 'inverted-secondary', branded: true }),
                'flex w-full items-center bg-transparent'
              )}
              href="/calendar"
            >
              View upcoming
            </AllPurposeLink>
          </div>
        </motion.div>
      </div>
      {items.length > 0 && (
        <motion.div
          ref={featuredRef}
          className="gap-md flex flex-col"
          variants={fadeInUp}
          initial="hidden"
          animate={featuredInView ? 'visible' : 'hidden'}
        >
          <div className="gap-md text-on-surface-inverted flex">
            <div>
              <OverlineHeader text="Featured Collection Of The Month" />
              <div className="gap-md flex items-baseline">
                <DisplayText>Fidenza</DisplayText>
                <AllPurposeLink
                  href={'/collection/fidenza-by-tyler-hobbs'}
                  className="text-on-surface-inverted-variant gap-xs flex items-center"
                >
                  <BodyText>View the collection</BodyText>
                  <ArrowRightIcon className="w-sm h-sm fill-on-surface-inverted-variant" />
                </AllPurposeLink>
              </div>
            </div>
          </div>
          <Bento500 items={items} />
        </motion.div>
      )}
    </div>
  )
}

const ChromieProgressBar = () => {
  return (
    <div
      className="h-2xs relative w-full overflow-hidden rounded-full bg-white/40"
      style={{
        boxShadow: '5px 0px 11.9px 0px rgba(177, 255, 124, 0.37), -7px 0px 11.3px 0px rgba(252, 158, 1, 0.18)'
      }}
    >
      {/* Cycling progress bar background */}
      <div
        className="chromie-progress-bar-gradient cycling absolute inset-0 h-full rounded-full"
        style={{
          width: '85%',
          boxShadow: '1px 0px 2.7px 0px rgba(0, 0, 0, 0.48)'
        }}
      />
    </div>
  )
}

const LinkArrowItem = ({ title, delay = 0, isInView }: { title: string; delay?: number; isInView?: boolean }) => {
  const linkVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay,
        ease: [0.21, 0.47, 0.32, 0.98]
      }
    }
  }

  return (
    <motion.button
      className="border-on-surface-inverted group w-full border-b"
      variants={linkVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="gap-xs py-xs flex items-center">
        <HeadlineText className="text-on-surface-inverted font-light">{title}</HeadlineText>
        <ArrowRightIcon className="fill-on-surface-inverted group-hover:translate-x-xs h-5 w-5 transition-transform duration-300" />
      </div>
    </motion.button>
  )
}
